import {
  kpiGoalPlanInputSchema,
  kpiGoalPlanResponseSchema,
  kpiGoalPreviewResponseSchema,
  kpiReportResponseSchema,
  kpiTrafficInputSchema,
  kpiTrafficResponseSchema,
  type KpiMetric,
  type KpiPeriod,
  type KpiReport,
  type SessionIdentity,
} from "@cosmo/contracts";
import { type FormEvent, useEffect, useMemo, useState } from "react";

const apiBaseUrl = (import.meta.env.VITE_API_BASE_URL || "").replace(/\/$/, "");

const periodLabels: Record<KpiPeriod, string> = {
  DAILY: "Diario",
  WEEKLY: "Semanal",
  MONTHLY: "Mensual",
  ANNUAL: "Anual",
};

function todayLocal(): string {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

async function responseMessage(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { error?: { message?: string } };
    return body.error?.message || "No fue posible completar la solicitud.";
  } catch {
    return "No fue posible completar la solicitud.";
  }
}

function money(value: number): string {
  return new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 0 }).format(value);
}

function shortDate(value: string): string {
  return new Intl.DateTimeFormat("es-MX", { day: "2-digit", month: "short", year: "numeric", timeZone: "UTC" }).format(new Date(`${value}T12:00:00Z`));
}

function metricValue(metric: KpiMetric | undefined): string {
  if (!metric) return "—";
  if (metric.unit === "CURRENCY") return money(metric.value);
  if (metric.unit === "PERCENT") return `${metric.value.toFixed(1)} %`;
  return new Intl.NumberFormat("es-MX", { maximumFractionDigits: 0 }).format(metric.value);
}

function goalValue(metric: KpiMetric): string {
  if (metric.goal === null) return "Sin meta";
  const value = metric.unit === "CURRENCY"
    ? money(metric.goal)
    : metric.unit === "PERCENT"
      ? `${metric.goal.toFixed(1)} %`
      : new Intl.NumberFormat("es-MX", { maximumFractionDigits: 0 }).format(metric.goal);
  return `${metric.goalDirection === "MAX" ? "Máximo" : "Mínimo"} ${value}`;
}

export function KpiOperations({ session, onClose }: { session: SessionIdentity; onClose: () => void }) {
  const [period, setPeriod] = useState<KpiPeriod>("MONTHLY");
  const [selectedDate, setSelectedDate] = useState(todayLocal());
  const [report, setReport] = useState<KpiReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [refresh, setRefresh] = useState(0);
  const [showTraffic, setShowTraffic] = useState(false);
  const [trafficDate, setTrafficDate] = useState(todayLocal());
  const [trafficQuantity, setTrafficQuantity] = useState("");
  const [trafficReason, setTrafficReason] = useState("");
  const [showGoals, setShowGoals] = useState(false);
  const [goalMonth, setGoalMonth] = useState(todayLocal().slice(0, 7));
  const [monthlyGoal, setMonthlyGoal] = useState("95000");
  const [goalReason, setGoalReason] = useState("");
  const [goalPreview, setGoalPreview] = useState<Record<string, number> | null>(null);
  const [saving, setSaving] = useState(false);

  const canWriteTraffic = session.permissions.includes("KPI_WRITE") && session.roles.some((role) => ["ASESOR", "OPTOMETRISTA", "GERENTE", "SUPERVISOR", "ADMINISTRADOR"].includes(role));
  const canManageGoals = session.roles.includes("ADMINISTRADOR") && session.permissions.includes("KPI_WRITE") && session.permissions.includes("ADMIN_WRITE");

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setMessage("");
    const query = new URLSearchParams({ period, date: selectedDate });
    fetch(`${apiBaseUrl}/api/v1/kpis/report?${query}`, {
      credentials: "include",
      headers: { Accept: "application/json" },
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) throw new Error(await responseMessage(response));
        return kpiReportResponseSchema.parse(await response.json()).data;
      })
      .then(setReport)
      .catch((cause: unknown) => {
        if (cause instanceof DOMException && cause.name === "AbortError") return;
        setReport(null);
        setMessage(cause instanceof Error ? cause.message : "No fue posible consultar los indicadores.");
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [period, refresh, selectedDate, session.branchId]);

  const groupedMetrics = useMemo(() => {
    const groups = new Map<string, KpiMetric[]>();
    for (const metric of report?.metrics || []) {
      const list = groups.get(metric.section) || [];
      list.push(metric);
      groups.set(metric.section, list);
    }
    return [...groups.entries()];
  }, [report]);

  const findMetric = (code: string) => report?.metrics.find((metric) => metric.code === code);

  async function saveTraffic(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaving(true);
    setMessage("");
    try {
      const input = kpiTrafficInputSchema.parse({
        date: trafficDate,
        quantity: Number(trafficQuantity),
        replacementReason: trafficReason.trim() || null,
      });
      const response = await fetch(`${apiBaseUrl}/api/v1/kpis/traffic`, {
        method: "POST",
        credentials: "include",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!response.ok) throw new Error(await responseMessage(response));
      const saved = kpiTrafficResponseSchema.parse(await response.json()).data;
      setMessage(saved.replaced ? "La corrección quedó guardada con su historial." : "El tráfico del día quedó registrado.");
      setTrafficQuantity("");
      setTrafficReason("");
      setShowTraffic(false);
      setRefresh((value) => value + 1);
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : "No fue posible guardar el tráfico.");
    } finally {
      setSaving(false);
    }
  }

  async function previewGoal() {
    setSaving(true);
    setMessage("");
    try {
      const input = kpiGoalPlanInputSchema.parse({ month: goalMonth, monthlyNetSales: Number(monthlyGoal), replacementReason: goalReason.trim() || null });
      const response = await fetch(`${apiBaseUrl}/api/v1/kpis/goals/preview`, {
        method: "POST",
        credentials: "include",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!response.ok) throw new Error(await responseMessage(response));
      setGoalPreview(kpiGoalPreviewResponseSchema.parse(await response.json()).data.targets);
    } catch (cause) {
      setGoalPreview(null);
      setMessage(cause instanceof Error ? cause.message : "No fue posible calcular el plan.");
    } finally {
      setSaving(false);
    }
  }

  async function saveGoal(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaving(true);
    setMessage("");
    try {
      const input = kpiGoalPlanInputSchema.parse({ month: goalMonth, monthlyNetSales: Number(monthlyGoal), replacementReason: goalReason.trim() || null });
      const response = await fetch(`${apiBaseUrl}/api/v1/kpis/goals`, {
        method: "POST",
        credentials: "include",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!response.ok) throw new Error(await responseMessage(response));
      kpiGoalPlanResponseSchema.parse(await response.json());
      setMessage("El plan mensual quedó guardado con su versión y responsable.");
      setGoalPreview(null);
      setGoalReason("");
      setShowGoals(false);
      setRefresh((value) => value + 1);
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : "No fue posible guardar el plan.");
    } finally {
      setSaving(false);
    }
  }

  if (loading && !report) return <section className="loading-panel"><span className="loading-ring" aria-hidden="true" /><h1>Cargando KPIs</h1><p>Calculando resultados de la sucursal y sus comparativos…</p></section>;

  return (
    <section className="admin-shell kpi-shell" aria-labelledby="kpi-title">
      <div className="section-heading">
        <div><p className="eyebrow">Resultados de sucursal</p><h1 id="kpi-title">KPIs y avance contra objetivos</h1><p>Ventas, conversión, productos y oportunidades calculados con la operación registrada.</p></div>
        <button className="secondary-button" onClick={onClose}>Volver al inicio</button>
      </div>

      <section className="operation-panel kpi-filter-panel">
        <div className="kpi-filters">
          <label>Periodo<select onChange={(event) => setPeriod(event.target.value as KpiPeriod)} value={period}>{Object.entries(periodLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
          <label>Fecha de corte<input max={todayLocal()} onChange={(event) => setSelectedDate(event.target.value)} type="date" value={selectedDate} /></label>
          <button className="secondary-button" onClick={() => setRefresh((value) => value + 1)}>Actualizar</button>
          {canWriteTraffic && <button className="primary-button" onClick={() => setShowTraffic((value) => !value)}>Capturar tráfico</button>}
          {canManageGoals && <button className="secondary-button" onClick={() => setShowGoals((value) => !value)}>Plan mensual</button>}
        </div>
        {report && <p className="kpi-period-caption">{report.branchName} · {shortDate(report.startDate)} al {shortDate(report.endDate)}</p>}
      </section>

      {message && <p className="form-message info-message" aria-live="polite">{message}</p>}

      {showTraffic && <form className="operation-panel kpi-editor" onSubmit={saveTraffic}>
        <div><h2>Tráfico de la sucursal</h2><p>Si corriges un día ya capturado, el motivo es obligatorio y el valor anterior permanece en auditoría.</p></div>
        <div className="field-grid"><label>Fecha<input disabled={saving} max={todayLocal()} onChange={(event) => setTrafficDate(event.target.value)} required type="date" value={trafficDate} /></label><label>Personas que ingresaron<input disabled={saving} min="0" onChange={(event) => setTrafficQuantity(event.target.value)} required type="number" value={trafficQuantity} /></label><label>Motivo de corrección<input disabled={saving} minLength={10} onChange={(event) => setTrafficReason(event.target.value)} placeholder="Solo cuando reemplazas una captura" value={trafficReason} /></label></div>
        <div className="form-actions"><button className="primary-button" disabled={saving}>{saving ? "Guardando…" : "Guardar tráfico"}</button><button className="text-button" onClick={() => setShowTraffic(false)} type="button">Cerrar</button></div>
      </form>}

      {showGoals && canManageGoals && <form className="operation-panel kpi-editor" onSubmit={saveGoal}>
        <div><h2>Plan mensual automático</h2><p>La meta de venta distribuye los objetivos diarios con mayor ponderación en fin de semana. Mínimo mensual: $95,000.</p></div>
        <div className="field-grid"><label>Mes<input disabled={saving} onChange={(event) => { setGoalMonth(event.target.value); setGoalPreview(null); }} required type="month" value={goalMonth} /></label><label>Meta de venta neta<input disabled={saving} min="95000" onChange={(event) => { setMonthlyGoal(event.target.value); setGoalPreview(null); }} required step="1" type="number" value={monthlyGoal} /></label><label>Motivo de sustitución<input disabled={saving} minLength={10} onChange={(event) => setGoalReason(event.target.value)} placeholder="Obligatorio si ya existe un plan" value={goalReason} /></label></div>
        {goalPreview && <div className="kpi-goal-preview"><span>Primeros pares <strong>{goalPreview.firstPairs}</strong></span><span>Tráfico <strong>{goalPreview.traffic}</strong></span><span>Transacciones <strong>{goalPreview.totalTransactions}</strong></span><span>Exámenes <strong>{goalPreview.exams}</strong></span></div>}
        <div className="form-actions"><button className="secondary-button" disabled={saving} onClick={previewGoal} type="button">Calcular plan</button><button className="primary-button" disabled={saving || !goalPreview}>{saving ? "Guardando…" : "Guardar plan"}</button><button className="text-button" onClick={() => setShowGoals(false)} type="button">Cerrar</button></div>
      </form>}

      {report && <>
        <div className="kpi-summary-grid">
          {["netSales", "firstPairs", "netConversion", "multifocalConversionPercent"].map((code) => { const metric = findMetric(code); return <article key={code}><span>{metric?.label || code}</span><strong>{metricValue(metric)}</strong><small>{metric ? goalValue(metric) : "Sin información"}</small>{metric?.progressPercent !== null && metric?.progressPercent !== undefined && <div className="kpi-progress"><i style={{ width: `${Math.min(100, Math.max(0, metric.progressPercent))}%` }} /></div>}</article>; })}
        </div>

        <section className={report.traffic.missingDates.length ? "operation-panel kpi-quality warning" : "operation-panel kpi-quality complete"}>
          <div><h2>Calidad del tráfico</h2><p>{report.traffic.capturedDays} de {report.traffic.expectedDays} días capturados.</p></div>
          <strong>{report.traffic.missingDates.length ? `${report.traffic.missingDates.length} pendiente${report.traffic.missingDates.length === 1 ? "" : "s"}` : "Periodo completo"}</strong>
          {report.traffic.missingDates.length > 0 && <small>Faltan: {report.traffic.missingDates.slice(0, 7).map(shortDate).join(", ")}{report.traffic.missingDates.length > 7 ? "…" : ""}</small>}
        </section>

        <section className="operation-panel">
          <div className="operation-panel-heading"><div><h2>Comparativos alineados</h2><p>Mismo tipo de periodo y días equivalentes para evitar comparaciones engañosas.</p></div></div>
          <div className="kpi-comparison-grid">{report.comparisons.map((comparison) => <article key={comparison.code}><span>{comparison.label}</span><strong>{money(comparison.netSales)}</strong><small>{comparison.firstPairs} primeros pares · {comparison.netConversion.toFixed(1)} % conversión neta · {comparison.multifocalConversion.toFixed(1)} % multifocal</small></article>)}</div>
        </section>

        <section className="operation-panel kpi-directory">
          <div className="operation-panel-heading"><div><h2>Indicadores de la sucursal</h2><p>{report.goalPlan ? `Plan ${report.goalPlan.month} · ${money(report.goalPlan.monthlyNetSales)} · ${report.goalPlan.createdByName}` : "No existe un plan mensual para esta fecha."}</p></div></div>
          {groupedMetrics.map(([section, metrics]) => <div className="kpi-section" key={section}><h3>{section}</h3><div className="kpi-table-wrap"><table className="kpi-table"><thead><tr><th>Indicador</th><th>Evaluado</th><th>Meta al corte</th><th>Estado</th></tr></thead><tbody>{metrics.map((metric) => <tr key={metric.code}><td>{metric.label}</td><td>{metricValue(metric)}{metric.volume !== null && metric.unit === "PERCENT" ? <small>{metric.volume.toFixed(0)} operaciones</small> : null}</td><td>{goalValue(metric)}</td><td><span className={`kpi-status ${metric.status.toLowerCase()}`}>{metric.status === "ON_TRACK" ? "Cumple" : metric.status === "OFF_TRACK" ? "Por atender" : "Informativo"}</span></td></tr>)}</tbody></table></div></div>)}
        </section>
      </>}
    </section>
  );
}
